import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import {
  MonthGrid, buildItemsForRange, buildCalendarItems,
  ymdKey, sameYMD, startOfDay, startOfWeek, addDays,
} from "./helpers";
import StructuralBackButton from "../shared/StructuralBackButton";
import { CustomIcon } from "../shared/CustomIcon";
import { Button } from "../shared/Button";
import { apiCreateTask } from "../tasks/api";

// ============================================================
// Calendar v3 — Year / Month / Week / Day view modes, Church/Mine/Both
// scope toggle, click-to-add (admin only), recurring events, birthdays,
// Add-to-Tasks on every item detail.
// ============================================================

// Map calendar item kind → gold CustomIcon name.
const KIND_ICON_NAME = { event: "event-ticket", service: "dove", appointment: "calendar-clock", blockout: "warning", birthday: "birthday-cake" };
const KIND_LABEL = { event: "Event", service: "Service", appointment: "Appointment", blockout: "Blockout", birthday: "Birthday" };
function KindIcon({ kind, size = 14 }) {
  const name = KIND_ICON_NAME[kind];
  if (!name) return <span aria-hidden="true">•</span>;
  return <CustomIcon name={name} size={size} />;
}
const KIND_ROUTE = (k) => k === "event" ? "/events" : k === "service" ? "/admin/services" : k === "appointment" ? "/appointments" : "/check-ins";

// Semantic Card left-border variant for calendar items.
//   service    → blue   (scheduled recurring)
//   event      → green when confirmed / default; amber for tentative/pending;
//                red when cancelled
//   appointment→ blue   (scheduled)
//   blockout   → red    (unavailable window)
//   birthday   → amber  (celebration highlight)
function kindVariant(it) {
  if (!it) return "none";
  if (it.kind === "service") return "blue";
  if (it.kind === "event") {
    const s = String(it.status || "").toLowerCase();
    if (s === "cancelled" || s === "canceled") return "red";
    if (s === "tentative" || s === "pending" || s === "draft") return "amber";
    return "green";
  }
  if (it.kind === "appointment") return "blue";
  if (it.kind === "blockout") return "red";
  if (it.kind === "birthday") return "amber";
  return "none";
}

// localStorage keys — persisted per user.
const LS_VIEW = (uid) => `kc_cal_view_${uid || "anon"}`;
const LS_SCOPE = (uid) => `kc_cal_scope_${uid || "anon"}`;

const VIEWS = ["year", "month", "week", "day"];
const SCOPES = ["church", "mine", "both"];

// Token-driven segmented control — pill-shaped container with equal segments.
// Active segment: background var(--kc-accent), color var(--kc-on-accent).
// Inactive: transparent bg, var(--kc-sub). No inner radius.
function SegmentedControl({ options, value, onChange, ariaLabel, testId, className = "" }) {
  return (
    <div
      className={`kc-segmented ${className}`}
      role="tablist"
      aria-label={ariaLabel}
      data-testid={testId}
    >
      {options.map(opt => (
        <button
          key={opt.value}
          type="button"
          role="tab"
          aria-selected={value === opt.value}
          data-active={value === opt.value}
          data-testid={opt.testId}
          className="kc-segmented-btn"
          onClick={() => onChange(opt.value)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

export function FullCalendarPage({
  currentUser, isSuperAdmin, isLimitedAdmin,
  events, services, appointments, blockouts, members, tasks, setTasks,
  navigate, onNewBlockout,
}) {
  const isAdmin = !!(isSuperAdmin || isLimitedAdmin);
  const uid = currentUser ? currentUser.id : "anon";

  // ----- Persisted view + scope -----
  const [viewMode, setViewMode] = useState(() => {
    const v = localStorage.getItem(LS_VIEW(uid));
    return VIEWS.includes(v) ? v : "month";
  });
  const [scope, setScope] = useState(() => {
    const s = localStorage.getItem(LS_SCOPE(uid));
    return SCOPES.includes(s) ? s : "both";
  });
  useEffect(() => { localStorage.setItem(LS_VIEW(uid), viewMode); }, [viewMode, uid]);
  useEffect(() => { localStorage.setItem(LS_SCOPE(uid), scope); }, [scope, uid]);

  // ----- Anchor date (drives all 4 views) -----
  const [anchor, setAnchor] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [dayModal, setDayModal] = useState(null);
  const [itemDetail, setItemDetail] = useState(null); // { item, date }
  const [addChooser, setAddChooser] = useState(null); // { date }
  const [toast, setToast] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 899px)");
    const handler = (e) => setIsMobile(e.matches);
    setIsMobile(mq.matches);
    if (mq.addEventListener) mq.addEventListener("change", handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", handler);
      else mq.removeListener(handler);
    };
  }, []);

  // ----- Range covering the current view (drives item build) -----
  const range = useMemo(() => {
    if (viewMode === "year") {
      return { from: new Date(anchor.getFullYear(), 0, 1), to: new Date(anchor.getFullYear(), 11, 31, 23, 59, 59, 999) };
    }
    if (viewMode === "week") {
      const s = startOfWeek(anchor);
      const e = addDays(s, 6); e.setHours(23, 59, 59, 999);
      return { from: s, to: e };
    }
    if (viewMode === "day") {
      return { from: startOfDay(anchor), to: new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate(), 23, 59, 59, 999) };
    }
    // month — also include the leading days of the visible grid (prev week buffer)
    const s = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const e = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0, 23, 59, 59, 999);
    return { from: s, to: e };
  }, [anchor, viewMode]);

  const { itemsByDate } = useMemo(
    () => buildItemsForRange({
      from: range.from, to: range.to,
      currentUser, scope,
      events, services, appointments, blockouts, members,
    }),
    [range, currentUser, scope, events, services, appointments, blockouts, members]
  );

  // Header label for the active view.
  const headerLabel = useMemo(() => {
    if (viewMode === "year") return String(anchor.getFullYear());
    if (viewMode === "week") {
      const s = startOfWeek(anchor); const e = addDays(s, 6);
      const sStr = s.toLocaleDateString("en-US", { month: "short", day: "numeric" });
      const eStr = e.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
      return `${sStr} – ${eStr}`;
    }
    if (viewMode === "day") return anchor.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
    return anchor.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  }, [anchor, viewMode]);

  // ----- Navigation -----
  const goToday = () => { const d = new Date(); setAnchor(d); setSelectedDate(d); };
  const goPrev = () => setAnchor(a => {
    const d = new Date(a);
    if (viewMode === "year") d.setFullYear(d.getFullYear() - 1);
    else if (viewMode === "week") d.setDate(d.getDate() - 7);
    else if (viewMode === "day") d.setDate(d.getDate() - 1);
    else d.setMonth(d.getMonth() - 1);
    return d;
  });
  const goNext = () => setAnchor(a => {
    const d = new Date(a);
    if (viewMode === "year") d.setFullYear(d.getFullYear() + 1);
    else if (viewMode === "week") d.setDate(d.getDate() + 7);
    else if (viewMode === "day") d.setDate(d.getDate() + 1);
    else d.setMonth(d.getMonth() + 1);
    return d;
  });

  // Click on a date cell:
  //   - Year view: jump to month view on that month
  //   - Month/Week/Day: select; if heavy day on month-grid open day modal
  const handleSelectDate = useCallback((date, items) => {
    setSelectedDate(date);
    setAnchor(date);
    if (viewMode === "year") { setViewMode("month"); return; }
    if (viewMode === "month" && !isMobile && items && items.length >= 5) {
      setDayModal({ date, items });
    }
  }, [viewMode, isMobile]);

  // Date-Add chooser: admins only.
  const openAddFor = (date) => {
    if (!isAdmin) return;
    setAddChooser({ date });
  };

  const navigateWith = (path, ymd) => {
    const url = `${path}${path.includes("?") ? "&" : "?"}new=1${ymd ? `&date=${ymd}` : ""}`;
    navigate(url);
  };

  const addItemToTasks = async (it) => {
    const dueYmd = it.date || (it.ref && it.ref.date) || "";
    const payload = {
      title: it.title || (KIND_LABEL[it.kind] || it.kind),
      description: it.meta || "",
      dueDate: dueYmd ? String(dueYmd).slice(0, 10) : "",
      repeat: "none",
      list: "Personal",
    };
    try {
      const fresh = await apiCreateTask(payload);
      if (typeof setTasks === "function") setTasks(prev => [...prev, fresh]);
      setToast("Added to your tasks.");
      setTimeout(() => setToast(null), 2400);
    } catch (e) {
      setToast(`Could not add task: ${e.message || e}`);
      setTimeout(() => setToast(null), 3500);
    }
  };

  const selKey = selectedDate ? ymdKey(selectedDate) : null;
  const selectedItems = (selKey && itemsByDate[selKey]) || [];

  return (
    <div data-testid="full-calendar-page" className="full-cal-page-v2 cal-v3">
      {/* Local styles: segmented control + semantic calendar-item card treatments. */}
      <style>{`
        .kc-segmented {
          display: inline-flex;
          align-items: stretch;
          background: var(--kc-elevated);
          border: 1px solid var(--kc-border);
          border-radius: 20px;
          padding: 3px;
          gap: 0;
          overflow: hidden;
        }
        .kc-segmented-btn {
          appearance: none;
          border: none;
          background: transparent;
          color: var(--kc-sub);
          border-radius: 0;
          padding: 6px 14px;
          font-family: 'Inter', sans-serif;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          cursor: pointer;
          transition: background 0.15s ease, color 0.15s ease;
          min-width: 56px;
        }
        .kc-segmented-btn:hover { color: var(--kc-text); }
        .kc-segmented-btn[data-active="true"] {
          background: var(--kc-accent);
          color: var(--kc-on-accent);
          border-radius: 18px;
        }
        .kc-toolbar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: var(--space-md);
          flex-wrap: wrap;
          margin-bottom: var(--space-md);
        }
        .kc-toolbar-nav {
          display: inline-flex;
          align-items: center;
          gap: var(--space-sm);
        }
        .kc-toolbar-title {
          font-family: 'Cinzel', serif;
          font-size: 18px;
          font-weight: 700;
          color: var(--kc-text);
          letter-spacing: 0.04em;
        }
        .dark .kc-toolbar-title {
          color: var(--kc-dark-accent);
        }
        .kc-cal-fab {
          position: fixed;
          right: 20px;
          bottom: 84px;
          width: 56px;
          height: 56px;
          border-radius: 50%;
          padding: 0;
          font-size: 24px;
          box-shadow: var(--kc-shadow-lg);
          z-index: 900;
        }
        @media (min-width: 900px) {
          .kc-cal-fab { bottom: 20px; }
        }
      `}</style>

      <div style={{ marginBottom: "var(--space-sm)" }}>
        <StructuralBackButton to="/check-ins" label="← Calendar Home" testId="full-cal-back" />
      </div>

      {/* Toolbar — header label + Today (secondary) + prev/next chevrons */}
      <div className="kc-toolbar">
        <div className="kc-toolbar-title" data-testid="full-cal-month">{headerLabel}</div>
        <div className="kc-toolbar-nav">
          <Button
            variant="secondary"
            size="sm"
            data-testid="full-cal-today"
            onClick={goToday}
          >
            Today
          </Button>
          <Button
            variant="icon"
            data-testid="full-cal-prev"
            onClick={goPrev}
            aria-label="Previous"
          >‹</Button>
          <Button
            variant="icon"
            data-testid="full-cal-next"
            onClick={goNext}
            aria-label="Next"
          >›</Button>
        </div>
      </div>

      {/* View-mode segmented control */}
      <div style={{ marginBottom: "var(--space-sm)", display: "flex", flexWrap: "wrap", gap: "var(--space-sm)" }}>
        <SegmentedControl
          ariaLabel="Calendar view"
          testId="cal-view-tabs"
          value={viewMode}
          onChange={setViewMode}
          options={VIEWS.map(v => ({
            value: v,
            label: v.charAt(0).toUpperCase() + v.slice(1),
            testId: `cal-view-${v}`,
          }))}
        />
        {/* Scope segmented control (Church / Mine / Both) */}
        <SegmentedControl
          ariaLabel="Calendar scope"
          testId="cal-scope-tabs"
          value={scope}
          onChange={setScope}
          options={SCOPES.map(s => ({
            value: s,
            label: s === "church" ? "Church" : s === "mine" ? "Mine" : "Both",
            testId: `cal-scope-${s}`,
          }))}
        />
      </div>

      {/* Body */}
      {viewMode === "year" && (
        <YearView year={anchor.getFullYear()} itemsByDate={itemsByDate} onJumpToMonth={(mIdx) => { setAnchor(new Date(anchor.getFullYear(), mIdx, 1)); setViewMode("month"); }} />
      )}

      {viewMode === "month" && (
        <div className="full-cal-layout">
          <div className="full-cal-grid-wrap">
            <MonthGrid
              year={anchor.getFullYear()}
              month={anchor.getMonth()}
              itemsByDate={itemsByDate}
              selectedDate={selectedDate}
              onSelectDate={handleSelectDate}
              today={new Date()}
              maxDots={3}
            />
          </div>
          {!isMobile && (
            <aside className="full-cal-detail" data-testid="full-cal-detail">
              <DayDetailPanel
                date={selectedDate}
                items={selectedItems}
                isAdmin={isAdmin}
                onOpenItem={(it) => setItemDetail({ item: it, date: selectedDate })}
                onOpenAdd={() => openAddFor(selectedDate)}
                onShowAll={() => setDayModal({ date: selectedDate, items: selectedItems })}
              />
            </aside>
          )}
        </div>
      )}

      {viewMode === "month" && isMobile && (
        <div className="cal-mobile-agenda" data-testid="cal-mobile-agenda">
          <DayDetailPanel
            date={selectedDate}
            items={selectedItems}
            isAdmin={isAdmin}
            dense
            onOpenItem={(it) => setItemDetail({ item: it, date: selectedDate })}
            onOpenAdd={() => openAddFor(selectedDate)}
          />
        </div>
      )}

      {viewMode === "week" && (
        <WeekView
          weekStart={startOfWeek(anchor)}
          itemsByDate={itemsByDate}
          selectedDate={selectedDate}
          onSelectDate={(d) => { setSelectedDate(d); setAnchor(d); }}
          onOpenItem={(it, d) => setItemDetail({ item: it, date: d })}
          onOpenDay={(d) => { setSelectedDate(d); setAnchor(d); setViewMode("day"); }}
          isAdmin={isAdmin}
          onOpenAdd={openAddFor}
        />
      )}

      {viewMode === "day" && (
        <DayView
          date={anchor}
          items={selectedItems}
          isAdmin={isAdmin}
          onOpenItem={(it) => setItemDetail({ item: it, date: anchor })}
          onOpenAdd={() => openAddFor(anchor)}
        />
      )}

      {/* Day overlay modal for heavily populated cells */}
      {dayModal && (
        <DayDetailModal
          date={dayModal.date}
          items={dayModal.items}
          isAdmin={isAdmin}
          onOpenAdd={() => { setDayModal(null); openAddFor(dayModal.date); }}
          onOpenItem={(it) => { setDayModal(null); setItemDetail({ item: it, date: dayModal.date }); }}
          onClose={() => setDayModal(null)}
        />
      )}

      {/* Item detail drawer w/ Add to Tasks */}
      {itemDetail && (
        <ItemDetailModal
          item={itemDetail.item}
          date={itemDetail.date}
          navigate={navigate}
          onAddToTasks={() => addItemToTasks(itemDetail.item)}
          onClose={() => setItemDetail(null)}
        />
      )}

      {/* Date-add chooser (admins only) */}
      {addChooser && isAdmin && (
        <AddChooserModal
          date={addChooser.date}
          onClose={() => setAddChooser(null)}
          onPick={(kind) => {
            const d = addChooser.date;
            setAddChooser(null);
            const ymd = ymdKey(d);
            if (kind === "event") navigateWith("/events", ymd);
            else if (kind === "service") navigateWith("/admin/services", ymd);
            else if (kind === "task") navigateWith("/tasks", ymd);
            else if (kind === "appointment") navigateWith("/appointments/manage", ymd);
            else if (kind === "blockout") { onNewBlockout && onNewBlockout(d); }
          }}
        />
      )}

      {/* Floating ADD button (admins only) — the ONE primary CTA on the page */}
      {isAdmin && (
        <Button
          variant="primary"
          data-testid="cal-add-fab"
          aria-label="Add to calendar"
          className="kc-cal-fab"
          onClick={() => openAddFor(selectedDate || new Date())}
        >+</Button>
      )}

      {/* Toast */}
      {toast && (
        <div data-testid="cal-toast" role="status" className="cal-toast">{toast}</div>
      )}
    </div>
  );
}

// ============================================================
// YearView — 12 mini months in a grid.
// ============================================================
function YearView({ year, itemsByDate, onJumpToMonth }) {
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return (
    <div className="cal-year-grid" data-testid="cal-year-grid">
      {monthNames.map((mName, mIdx) => {
        const dim = new Date(year, mIdx + 1, 0).getDate();
        const startDow = new Date(year, mIdx, 1).getDay();
        const cells = [];
        for (let i = 0; i < startDow; i++) cells.push(null);
        for (let d = 1; d <= dim; d++) {
          const k = `${year}-${String(mIdx + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
          const items = itemsByDate[k] || [];
          cells.push({ d, items });
        }
        return (
          <div
            key={mName}
            role="button"
            tabIndex={0}
            data-testid={`cal-year-month-${mIdx}`}
            className="cal-year-month"
            onClick={() => onJumpToMonth(mIdx)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onJumpToMonth(mIdx); } }}
          >
            <div className="cymo-title">{mName} {year}</div>
            <div className="cymo-grid">
              {["S","M","T","W","T","F","S"].map((w, i) => <div key={`y-h-${i}`} className="cymo-h">{w}</div>)}
              {cells.map((c, i) => {
                if (!c) return <div key={`y-e-${i}`} className="cymo-cell empty"/>;
                const k = c.items[0] ? c.items[0].kind : null;
                return (
                  <div key={`y-d-${c.d}`} className={`cymo-cell ${c.items.length ? "has-items" : ""}`}>
                    <span className="cymo-num">{c.d}</span>
                    {c.items.length > 0 && <span className={`cymo-dot kind-${k}`} />}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ============================================================
// WeekView — 7-day grid with all-day strip + hourly timeline.
// ============================================================
function WeekView({ weekStart, itemsByDate, selectedDate, onSelectDate, onOpenItem, onOpenDay, isAdmin, onOpenAdd }) {
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  // Build a 6am–10pm hourly grid (17 rows).
  const hours = Array.from({ length: 17 }, (_, i) => i + 6);
  const fmtHour = (h) => `${(h % 12) || 12}${h < 12 ? "a" : "p"}`;

  // Returns hour 0..23 from time string "HH:MM" or null.
  const parseHour = (t) => {
    if (!t) return null;
    const m = /^(\d{1,2}):(\d{2})/.exec(String(t));
    if (!m) return null;
    return Math.max(0, Math.min(23, Number(m[1])));
  };

  return (
    <div className="cal-week-view" data-testid="cal-week-view">
      <div className="cal-week-head">
        <div className="cal-week-corner" />
        {days.map((d, i) => {
          const isSel = sameYMD(d, selectedDate);
          const isToday = sameYMD(d, new Date());
          return (
            <div
              key={i}
              role="button"
              tabIndex={0}
              data-testid={`cal-week-head-${i}`}
              className={`cal-week-col-head ${isSel ? "is-selected" : ""} ${isToday ? "is-today" : ""}`}
              onClick={() => onSelectDate(d)}
              onDoubleClick={() => onOpenDay(d)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelectDate(d); } }}
            >
              <div className="cwch-day">{d.toLocaleDateString("en-US", { weekday: "short" }).slice(0, 3)}</div>
              <div className="cwch-num">{d.getDate()}</div>
            </div>
          );
        })}
      </div>

      {/* All-day strip (birthdays, blockouts all-day, events without time) */}
      <div className="cal-week-allday">
        <div className="cal-week-time-col cal-week-allday-label">All-day</div>
        {days.map((d, i) => {
          const k = ymdKey(d);
          const items = (itemsByDate[k] || []).filter(it => !it.time || it.kind === "birthday" || it.allDay);
          return (
            <div key={`ad-${i}`} className="cal-week-allday-cell" data-testid={`cal-week-allday-${i}`}>
              {items.map((it, idx) => (
                <button
                  key={`${it.kind}-${it.id}-${idx}`}
                  type="button"
                  className={`cal-block kind-${it.kind}`}
                  data-testid={`cal-week-item-${it.kind}-${it.id}-${i}`}
                  onClick={() => onOpenItem(it, d)}
                  title={it.title}
                >
                  <span className="cal-block-ico"><KindIcon kind={it.kind} size={12} /></span>
                  <span className="cal-block-title">{it.title}</span>
                </button>
              ))}
              {isAdmin && items.length === 0 && (
                <button type="button" className="cal-week-add-empty" data-testid={`cal-week-add-${i}`} onClick={() => onOpenAdd(d)} aria-label={`Add to ${d.toDateString()}`}>+</button>
              )}
            </div>
          );
        })}
      </div>

      {/* Hourly grid */}
      <div className="cal-week-body">
        {hours.map(h => (
          <div key={`h-${h}`} className="cal-week-row">
            <div className="cal-week-time-col">{fmtHour(h)}</div>
            {days.map((d, i) => {
              const k = ymdKey(d);
              const items = (itemsByDate[k] || []).filter(it => parseHour(it.time) === h);
              return (
                <div key={`h-${h}-${i}`} className="cal-week-cell">
                  {items.map((it, idx) => (
                    <button
                      key={`${it.kind}-${it.id}-${idx}`}
                      type="button"
                      className={`cal-block kind-${it.kind}`}
                      data-testid={`cal-week-slot-${it.kind}-${it.id}-${h}`}
                      onClick={() => onOpenItem(it, d)}
                      title={`${it.time || ""} ${it.title}`}
                    >
                      <span className="cal-block-ico"><KindIcon kind={it.kind} size={12} /></span>
                      <span className="cal-block-title">{it.title}</span>
                    </button>
                  ))}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================
// DayView — single column hourly timeline.
// ============================================================
function DayView({ date, items, isAdmin, onOpenItem, onOpenAdd }) {
  const hours = Array.from({ length: 17 }, (_, i) => i + 6);
  const fmtHour = (h) => `${(h % 12) || 12}${h < 12 ? "a" : "p"}`;
  const parseHour = (t) => {
    if (!t) return null;
    const m = /^(\d{1,2}):(\d{2})/.exec(String(t));
    if (!m) return null;
    return Math.max(0, Math.min(23, Number(m[1])));
  };
  const allDay = items.filter(it => !it.time || it.kind === "birthday" || it.allDay);

  return (
    <div className="cal-day-view" data-testid="cal-day-view">
      <div className="cal-day-header">
        <div className="cal-day-title">{date.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</div>
        {isAdmin && (
          <Button variant="secondary" size="sm" data-testid="cal-day-add" onClick={onOpenAdd}>+ Add</Button>
        )}
      </div>

      {allDay.length > 0 && (
        <div className="cal-day-allday">
          <div className="cal-day-allday-label">All-day</div>
          <div className="cal-day-allday-items">
            {allDay.map((it, idx) => (
              <button
                key={`ad-${it.kind}-${it.id}-${idx}`}
                type="button"
                className={`cal-block kind-${it.kind}`}
                data-testid={`cal-day-allday-item-${it.kind}-${it.id}`}
                onClick={() => onOpenItem(it)}
              >
                <span className="cal-block-ico"><KindIcon kind={it.kind} size={12} /></span>
                <span className="cal-block-title">{it.title}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="cal-day-body">
        {hours.map(h => {
          const slot = items.filter(it => parseHour(it.time) === h);
          return (
            <div key={`d-h-${h}`} className="cal-day-row">
              <div className="cal-day-time">{fmtHour(h)}</div>
              <div className="cal-day-slot">
                {slot.map((it, idx) => (
                  <button
                    key={`${it.kind}-${it.id}-${idx}`}
                    type="button"
                    className={`cal-block kind-${it.kind}`}
                    data-testid={`cal-day-slot-${it.kind}-${it.id}-${h}`}
                    onClick={() => onOpenItem(it)}
                  >
                    <span className="cal-block-ico"><KindIcon kind={it.kind} size={12} /></span>
                    <span className="cal-block-title">{it.time ? `${it.time} · ` : ""}{it.title}</span>
                  </button>
                ))}
                {isAdmin && slot.length === 0 && (
                  <button type="button" className="cal-day-add-slot" data-testid={`cal-day-add-slot-${h}`} onClick={onOpenAdd} aria-label="Add">+</button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ----- Day detail panel (used by month-view side panel + mobile agenda) -----
function DayDetailPanel({ date, items, isAdmin, dense, onOpenItem, onOpenAdd, onShowAll }) {
  const LIMIT = dense ? 6 : 8;
  const visible = items.slice(0, LIMIT);
  const overflow = items.length > LIMIT;
  return (
    <div data-testid="day-detail-panel">
      <div className="full-cal-detail-date" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "var(--space-sm)" }}>
        <span>{date ? date.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" }) : "Pick a date"}</span>
        {isAdmin && (
          <Button variant="secondary" size="sm" data-testid="day-detail-add-btn" onClick={onOpenAdd} aria-label="Add to this day">+ Add</Button>
        )}
      </div>
      {items.length === 0 ? (
        <div className="full-cal-detail-empty" data-testid="day-detail-empty">No items scheduled for this day.</div>
      ) : (
        <div className="full-cal-detail-list">
          {visible.map((it, i) => (
            <div
              key={`${it.kind}-${it.id}-${i}`}
              role="button"
              tabIndex={0}
              className={`full-cal-item kind-${it.kind} card-${kindVariant(it)}`}
              data-testid={`full-cal-item-${it.kind}-${it.id}`}
              onClick={() => onOpenItem(it)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpenItem(it); } }}
            >
              <div className="fci-kind"><span className="fci-kind-ico" aria-hidden="true"><KindIcon kind={it.kind} size={14} /></span> {it.kind}</div>
              <div className="fci-title">{it.title}</div>
              {it.meta && <div className="fci-meta">{it.meta}</div>}
            </div>
          ))}
          {overflow && (
            <button type="button" data-testid="day-detail-show-all" className="full-cal-show-all" onClick={onShowAll}>View all {items.length} items →</button>
          )}
        </div>
      )}
    </div>
  );
}

// ----- Overlay modal: shown when a day has many items -----
function DayDetailModal({ date, items, isAdmin, onOpenItem, onOpenAdd, onClose }) {
  return (
    <div className="modal-overlay" data-testid="day-detail-modal" onClick={onClose}>
      <div className="modal-card cal-day-modal cal-modal-scrollable" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="cal-day-modal-header">
          <div className="cal-day-modal-title">{date.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</div>
          <div className="flex gap-8 items-center">
            {isAdmin && <Button variant="secondary" size="sm" data-testid="day-modal-add-btn" onClick={onOpenAdd}>+ Add</Button>}
            <button type="button" className="cal-day-modal-close" data-testid="day-detail-modal-close" onClick={onClose} aria-label="Close">×</button>
          </div>
        </div>
        <div className="cal-day-modal-body">
          {items.length === 0 ? (
            <div className="full-cal-detail-empty">No items scheduled for this day.</div>
          ) : (
            <div className="full-cal-detail-list">
              {items.map((it, i) => (
                <div
                  key={`${it.kind}-${it.id}-${i}`}
                  role="button"
                  tabIndex={0}
                  className={`full-cal-item kind-${it.kind} card-${kindVariant(it)}`}
                  data-testid={`day-modal-item-${it.kind}-${it.id}`}
                  onClick={() => onOpenItem(it)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpenItem(it); } }}
                >
                  <div className="fci-kind"><span className="fci-kind-ico" aria-hidden="true"><KindIcon kind={it.kind} size={14} /></span> {it.kind}</div>
                  <div className="fci-title">{it.title}</div>
                  {it.meta && <div className="fci-meta">{it.meta}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ----- Item Detail Modal — includes the Add-to-Tasks gold button -----
function ItemDetailModal({ item, date, navigate, onAddToTasks, onClose }) {
  if (!item) return null;
  const goTo = () => {
    onClose();
    if (item.kind === "birthday" && item.memberId) {
      navigate(`/members/${item.memberId}`);
      return;
    }
    navigate(KIND_ROUTE(item.kind));
  };
  return (
    <div className="modal-overlay" data-testid="cal-item-detail-modal" onClick={onClose}>
      <div className="modal-card cal-modal-scrollable" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="cal-day-modal-header">
          <div className="cal-day-modal-title">
            <span style={{ marginRight: 8, display: "inline-flex" }}><KindIcon kind={item.kind} size={14} /></span>{item.title}
          </div>
          <button type="button" className="cal-day-modal-close" data-testid="cal-item-detail-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="cal-day-modal-body">
          <div className="text-card-secondary" style={{ marginBottom: "var(--space-sm)" }}>
            <div><strong>Type:</strong> {item.kind}</div>
            <div><strong>Date:</strong> {date ? date.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" }) : "—"}</div>
            {item.time && <div><strong>Time:</strong> {item.time}</div>}
            {item.meta && <div><strong>Details:</strong> {item.meta}</div>}
          </div>
          <div className="flex flex-wrap" style={{ gap: "var(--space-sm)" }}>
            <Button
              variant="primary"
              data-testid="cal-item-add-to-tasks"
              onClick={onAddToTasks}
            >+ Add to Tasks</Button>
            <Button
              variant="secondary"
              data-testid="cal-item-open-link"
              onClick={goTo}
            >Open {item.kind === "birthday" ? "Profile" : "Page"}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ----- Add chooser modal — shown when admin clicks the gold "+" -----
function AddChooserModal({ date, onClose, onPick }) {
  const label = date.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
  return (
    <div className="modal-overlay" data-testid="cal-add-chooser" onClick={onClose}>
      <div className="modal-card cal-modal-scrollable" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" style={{ maxWidth: 440 }}>
        <div className="cal-day-modal-header">
          <div className="cal-day-modal-title">Add to {label}</div>
          <button type="button" className="cal-day-modal-close" data-testid="cal-add-chooser-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="cal-day-modal-body">
          <div className="flex" style={{ flexDirection: "column", gap: 10 }}>
            <button type="button" className="cal-chooser-row" data-testid="cal-add-event" onClick={() => onPick("event")}>
              <span className="cfi-ico"><CustomIcon name="event-ticket" size={20} /></span>
              <span className="cfi-text"><span className="cfi-title">Add Church Event</span><span className="cfi-sub">Conference, gathering, special service</span></span>
            </button>
            <button type="button" className="cal-chooser-row" data-testid="cal-add-service" onClick={() => onPick("service")}>
              <span className="cfi-ico"><CustomIcon name="dove" size={20} /></span>
              <span className="cfi-text"><span className="cfi-title">Add Recurring Service</span><span className="cfi-sub">Weekly / monthly service</span></span>
            </button>
            <button type="button" className="cal-chooser-row" data-testid="cal-add-task" onClick={() => onPick("task")}>
              <span className="cfi-ico"><CustomIcon name="check" size={20} /></span>
              <span className="cfi-text"><span className="cfi-title">Add Task</span><span className="cfi-sub">Personal to-do with due date</span></span>
            </button>
            <button type="button" className="cal-chooser-row" data-testid="cal-add-appointment" onClick={() => onPick("appointment")}>
              <span className="cfi-ico"><CustomIcon name="calendar-clock" size={20} /></span>
              <span className="cfi-text"><span className="cfi-title">Add Appointment</span><span className="cfi-sub">Book a pastoral or member appointment</span></span>
            </button>
            <button type="button" className="cal-chooser-row" data-testid="cal-add-blockout" onClick={() => onPick("blockout")}>
              <span className="cfi-ico"><CustomIcon name="warning" size={20} /></span>
              <span className="cfi-text"><span className="cfi-title">Add Blockout</span><span className="cfi-sub">Unavailable window</span></span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
